// Same convention as ../roja-app/src/utils/currency.ts — every price shows
// US$ (ZWG is a per-listing detail the app itself renders, not something
// the admin console's aggregate figures need).

export function usd(amount: number): string {
  return "US$" + Math.round(amount).toLocaleString("en-US");
}

/** PocketBase dates serialize as `"YYYY-MM-DD HH:MM:SS.sssZ"` strings (or `""`/undefined when unset), not a Firestore Timestamp object. */
export function toDate(ts: string | undefined | null): Date | null {
  if (!ts) return null;
  const d = new Date(ts.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(ts: string | undefined | null): string {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(ts: string | undefined | null): string {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatRelative(ts: string | undefined | null): string {
  const d = toDate(ts);
  if (!d) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatDate(ts);
}

/** "YYYY-MM-DD" in UTC, matching the `date` field pocketbase/pb_hooks/analytics.pb.js writes. */
export function utcDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Turns a `users.lastActiveAt` ping (see roja-app/src/pocketbase/presence.ts,
 * which writes it, and roja-app/src/utils/presence.ts, its app-side twin)
 * into {online, label} for a status dot. There's no separate online
 * boolean anywhere — online is just "pinged recently enough".
 */
const ONLINE_WITHIN_MS = 3 * 60 * 1000; // matches the app's ~60s ping interval plus slack for one missed beat

export function presenceFromLastActiveAt(lastActiveAt: string | undefined | null): { online: boolean; label: string } {
  const d = toDate(lastActiveAt);
  if (!d) return { online: false, label: "Offline" };

  const diffMs = Date.now() - d.getTime();
  if (diffMs < ONLINE_WITHIN_MS) return { online: true, label: "Online" };
  return { online: false, label: `Last seen ${formatRelative(lastActiveAt)}` };
}
